import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Save, RefreshCw, Search, Plus, Trash2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const pages = [
  { id: 'homepage', name: 'Homepage', path: '/' },
  { id: 'about', name: 'About Us', path: '/about' },
  { id: 'services', name: 'Services', path: '/services' },
  { id: 'products', name: 'Products', path: '/products' },
  { id: 'gallery', name: 'Gallery', path: '/gallery' },
  { id: 'reviews', name: 'Reviews', path: '/reviews' },
  { id: 'contact', name: 'Contact', path: '/contact' },
];

export const SEOManager = () => {
  const { token } = useAuth();
  const [seoSettings, setSeoSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('homepage');

  useEffect(() => { fetchAllSettings(); }, []);

  const fetchAllSettings = async () => {
    try {
      const settings = {};
      for (const page of pages) {
        try {
          const res = await apiService.getSetting(token, page.id);
          settings[page.id] = {
            seo_title: res.data?.seo_title || '',
            seo_description: res.data?.seo_description || '',
            seo_keywords: res.data?.seo_keywords || [],
            og_image: res.data?.og_image || ''
          };
        } catch {
          settings[page.id] = { seo_title: '', seo_description: '', seo_keywords: [], og_image: '' };
        }
      }
      setSeoSettings(settings);
    } catch (error) {
      toast.error('Failed to load SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (pageId) => {
    setSaving(true);
    try {
      const currentSettings = seoSettings[pageId];
      await apiService.updateSetting(token, pageId, currentSettings);
      toast.success(`SEO settings saved for ${pages.find(p => p.id === pageId)?.name}!`);
    } catch (error) {
      toast.error('Error saving SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePageSEO = (pageId, field, value) => {
    setSeoSettings({
      ...seoSettings,
      [pageId]: { ...seoSettings[pageId], [field]: value }
    });
  };

  const addKeyword = (pageId) => {
    const keywords = [...(seoSettings[pageId]?.seo_keywords || []), ''];
    updatePageSEO(pageId, 'seo_keywords', keywords);
  };

  const updateKeyword = (pageId, index, value) => {
    const keywords = [...seoSettings[pageId].seo_keywords];
    keywords[index] = value;
    updatePageSEO(pageId, 'seo_keywords', keywords);
  };

  const removeKeyword = (pageId, index) => {
    const keywords = seoSettings[pageId].seo_keywords.filter((_, i) => i !== index);
    updatePageSEO(pageId, 'seo_keywords', keywords);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="seo-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">SEO Manager</h1>
          <p className="text-slate-500">Optimize your pages for search engines</p>
        </div>
      </div>

      {/* SEO Tips */}
      <Card className="bg-sky-50 border-sky-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Search className="w-5 h-5 text-sky-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-sky-900">SEO Best Practices</p>
              <ul className="text-sky-700 mt-1 space-y-1">
                <li>• Title should be 50-60 characters</li>
                <li>• Description should be 150-160 characters</li>
                <li>• Use relevant keywords naturally</li>
                <li>• Each page should have unique title and description</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto p-1 bg-white shadow-sm">
          {pages.map(page => (
            <TabsTrigger key={page.id} value={page.id} className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              {page.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {pages.map(page => (
          <TabsContent key={page.id} value={page.id}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  {page.name} SEO Settings
                </CardTitle>
                <CardDescription>Path: {page.path}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Meta Title</Label>
                  <Input
                    value={seoSettings[page.id]?.seo_title || ''}
                    onChange={(e) => updatePageSEO(page.id, 'seo_title', e.target.value)}
                    placeholder="Page title for search engines"
                    maxLength={70}
                  />
                  <p className="text-xs text-slate-500">
                    {seoSettings[page.id]?.seo_title?.length || 0}/60 characters (recommended)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Meta Description</Label>
                  <Textarea
                    value={seoSettings[page.id]?.seo_description || ''}
                    onChange={(e) => updatePageSEO(page.id, 'seo_description', e.target.value)}
                    placeholder="Brief description for search engine results"
                    rows={3}
                    maxLength={200}
                  />
                  <p className="text-xs text-slate-500">
                    {seoSettings[page.id]?.seo_description?.length || 0}/160 characters (recommended)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Keywords</Label>
                  <div className="space-y-2">
                    {seoSettings[page.id]?.seo_keywords?.map((keyword, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={keyword}
                          onChange={(e) => updateKeyword(page.id, index, e.target.value)}
                          placeholder="Keyword"
                        />
                        <Button variant="ghost" size="icon" onClick={() => removeKeyword(page.id, index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => addKeyword(page.id)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Keyword
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>OG Image (Social Share Image)</Label>
                  {seoSettings[page.id]?.og_image && (
                    <img src={seoSettings[page.id].og_image} alt="OG Preview" className="w-full max-w-md h-40 object-cover rounded-lg" />
                  )}
                  <Input
                    value={seoSettings[page.id]?.og_image || ''}
                    onChange={(e) => updatePageSEO(page.id, 'og_image', e.target.value)}
                    placeholder="Image URL for social media sharing"
                  />
                  <p className="text-xs text-slate-500">Recommended: 1200x630 pixels</p>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => handleSave(page.id)} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
                    <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save SEO Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default SEOManager;
